import {
  CampaignLevelOfWarfare,
  CampaignStatus,
  ClassificationLevel,
  CoalitionPartner,
  KillChainCategory,
  LeadCommand,
  OperationalRegion,
} from '@@types/api.types';
import {
  LONG_DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
} from '@azymmetric/shared/validation/validation.constants';
import { z } from 'zod';

const TAG_MAX_LENGTH = 120;
const ENUM_ARRAY_MAX_ITEMS = 50;
const EVIDENCE_MAX_LENGTH = 500;
const EVIDENCE_MAX_ITEMS = 50;
const DECISIVE_POINT_MAX_ITEMS = 50;
const SCENARIO_MAX_ITEMS = 100;
const VIGNETTE_MAX_ITEMS = 100;

const requiredName = z
  .string()
  .trim()
  .min(NAME_MIN_LENGTH, `Name must be at least ${NAME_MIN_LENGTH} characters.`)
  .max(NAME_MAX_LENGTH, `Name must be at most ${NAME_MAX_LENGTH} characters.`);

const optionalDescription = z
  .string()
  .max(LONG_DESCRIPTION_MAX_LENGTH, `Description must be at most ${LONG_DESCRIPTION_MAX_LENGTH} characters.`);

const optionalRationale = z
  .string()
  .max(LONG_DESCRIPTION_MAX_LENGTH, `Rationale must be at most ${LONG_DESCRIPTION_MAX_LENGTH} characters.`);

const optionalTag = z.string().max(TAG_MAX_LENGTH, `Value must be at most ${TAG_MAX_LENGTH} characters.`);

const evidenceList = z
  .array(z.string().max(EVIDENCE_MAX_LENGTH, `Evidence entry must be at most ${EVIDENCE_MAX_LENGTH} characters.`))
  .max(EVIDENCE_MAX_ITEMS, `Too many evidence entries (max ${EVIDENCE_MAX_ITEMS}).`);

const operationalRegionsArray = z
  .array(z.nativeEnum(OperationalRegion))
  .max(ENUM_ARRAY_MAX_ITEMS, `Too many entries (max ${ENUM_ARRAY_MAX_ITEMS}).`);

const coalitionPartnersArray = z
  .array(z.nativeEnum(CoalitionPartner))
  .max(ENUM_ARRAY_MAX_ITEMS, `Too many entries (max ${ENUM_ARRAY_MAX_ITEMS}).`);

const leadCommandsArray = z
  .array(z.nativeEnum(LeadCommand))
  .max(ENUM_ARRAY_MAX_ITEMS, `Too many entries (max ${ENUM_ARRAY_MAX_ITEMS}).`);

export const campaignDecisivePointDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'Description is required.')
  .max(LONG_DESCRIPTION_MAX_LENGTH, `Description must be at most ${LONG_DESCRIPTION_MAX_LENGTH} characters.`);

const campaignDecisivePointDraftSchema = z.object({
  clientId: z.string(),
  description: campaignDecisivePointDescriptionSchema,
  evidence: evidenceList,
  id: z.string().optional(),
  location: optionalTag,
  position: z.number().int().min(1).max(5),
});

const campaignVignetteDraftSchema = z
  .object({
    clientId: z.string(),
    description: optionalDescription,
    id: z.string().optional(),
    missions: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        studyId: z.string(),
        studyName: z.string().nullable(),
      }),
    ),
    name: requiredName,
    operationalRegionsOverride: operationalRegionsArray,
    operationalRegionsOverrideRationale: optionalRationale,
  })
  .superRefine((vignette, ctx) => {
    if (vignette.operationalRegionsOverride.length > 0 && vignette.operationalRegionsOverrideRationale.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Region override rationale is required when overrides are set.',
        path: ['operationalRegionsOverrideRationale'],
      });
    }
  });

const campaignScenarioDraftSchema = z
  .object({
    classificationLevel: z.nativeEnum(ClassificationLevel),
    clientId: z.string(),
    description: optionalDescription,
    id: z.string().optional(),
    levelOfWarfare: z.nativeEnum(CampaignLevelOfWarfare),
    levelOfWarfareOverrideRationale: optionalRationale,
    name: requiredName,
    operationalRegions: operationalRegionsArray,
    vignettes: z
      .array(campaignVignetteDraftSchema)
      .max(VIGNETTE_MAX_ITEMS, `Too many vignettes (max ${VIGNETTE_MAX_ITEMS}).`),
  })
  .superRefine((scenario, ctx) => {
    if (
      scenario.levelOfWarfare !== CampaignLevelOfWarfare.OPERATIONAL &&
      scenario.levelOfWarfareOverrideRationale.trim() === ''
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Override rationale is required when level differs from Operational.',
        path: ['levelOfWarfareOverrideRationale'],
      });
    }
  });

export const campaignDraftSchema = z.object({
  classificationLevel: z.nativeEnum(ClassificationLevel),
  coalitionPartners: coalitionPartnersArray,
  consumedStudyIds: z.array(z.string()),
  decisivePoints: z
    .array(campaignDecisivePointDraftSchema)
    .max(DECISIVE_POINT_MAX_ITEMS, `Too many decisive points (max ${DECISIVE_POINT_MAX_ITEMS}).`),
  description: optionalDescription,
  killChainCategory: z.nativeEnum(KillChainCategory).nullable(),
  leadCommands: leadCommandsArray,
  name: requiredName,
  scenarios: z
    .array(campaignScenarioDraftSchema)
    .max(SCENARIO_MAX_ITEMS, `Too many scenarios (max ${SCENARIO_MAX_ITEMS}).`),
  status: z.nativeEnum(CampaignStatus),
  timeWindow: optionalTag,
});

export const campaignNameSchema = requiredName;
export const campaignScenarioNameSchema = requiredName;
export const campaignVignetteNameSchema = requiredName;
